/**
 * Mock data and constants for the guided selling deal flow.
 *
 * Entities: PackageTemplate, CreativeSet, Proposal, Agreement
 * Plus: objectives, tones, formats, deal stages, helpers.
 */

import {
  MapPin, Zap, Building2, Shield, TrendingUp,
  Users, PartyPopper, Calendar, Navigation, Hash, Store,
} from 'lucide-react';
import {
  MOCK_BUSINESS as STUDIO_HEARTH_BUSINESS,
  MOCK_PHOTOS as STUDIO_HEARTH_PHOTOS,
  MOCK_VIDEO_URL as STUDIO_HEARTH_VIDEO_URL,
} from '@/services/mockStudioData';

// ── Package templates (seller picks one) ─────────────────────────────────────

export const PACKAGE_TEMPLATES = [
  {
    id: 'pkg-starter',
    name: 'Local Awareness Starter',
    tagline: 'Stay visible locally',
    description: 'Perfect for businesses that want consistent local presence on streaming TV.',
    budgetRange: { min: 500, max: 3000, default: 1500 },
    flightDefault: 2, // weeks
    targetingDefault: 'around_location',
    icon: MapPin,
    recommended: true,
  },
  {
    id: 'pkg-promo',
    name: 'Seasonal Promo Burst',
    tagline: 'Get attention fast',
    description: 'Short, high-impact burst for promotions, grand openings, or seasonal pushes.',
    budgetRange: { min: 1000, max: 5000, default: 2500 },
    flightDefault: 1,
    targetingDefault: 'around_location',
    icon: Zap,
    recommended: false,
  },
  {
    id: 'pkg-multi',
    name: 'Multi-Location Brand Lift',
    tagline: 'One message, many stores',
    description: 'Reach customers across multiple locations with a unified brand campaign.',
    budgetRange: { min: 3000, max: 15000, default: 7500 },
    flightDefault: 4,
    targetingDefault: 'city_wide',
    icon: Building2,
    recommended: false,
  },
  {
    id: 'pkg-defend',
    name: 'Defend Your Area',
    tagline: 'Reach customers near key places',
    description: 'Geo-targeted campaign around competitor locations or high-traffic points of interest.',
    budgetRange: { min: 1500, max: 8000, default: 3000 },
    flightDefault: 3,
    targetingDefault: 'custom_address',
    icon: Shield,
    recommended: false,
  },
];

// ── Pitch Kit options ────────────────────────────────────────────────────────

export const OBJECTIVES = [
  { id: 'awareness', label: 'Get more customers nearby', icon: Users },
  { id: 'grand_opening', label: 'Grand opening', icon: PartyPopper },
  { id: 'seasonal', label: 'Seasonal promo', icon: Calendar },
];

export const TONES = ['Auto', 'Professional', 'Friendly', 'Energetic'];

export const FORMATS = [
  { id: 'ctv_16_9', label: 'TV (16:9)', default: true },
  { id: 'social_9_16', label: 'Social (9:16)', default: false },
  { id: 'social_1_1', label: 'Social (1:1)', default: false },
];

// ── Plan customization options ───────────────────────────────────────────────

export const FLIGHT_OPTIONS = [
  { weeks: 1, label: '1 week' },
  { weeks: 2, label: '2 weeks' },
  { weeks: 4, label: '4 weeks' },
  { weeks: 8, label: '8 weeks' },
];

export const TARGET_AREA_OPTIONS = [
  { id: 'around_location', label: 'Around this location' },
  { id: 'custom_address', label: 'Around a different address' },
  { id: 'city_wide', label: 'City-wide' },
];

// ── Deal stages (for Stepper) ────────────────────────────────────────────────

export const DEAL_STAGES = [
  { key: 'discovery', title: 'Find Business' },
  { key: 'pitch', title: 'Build Pitch' },
  { key: 'plan', title: 'Choose Plan' },
  { key: 'proposal', title: 'Send Proposal' },
  { key: 'close', title: 'Close & Launch' },
];

/** V2 deal stages — updated labels for seller operating system */
export const DEAL_STAGES_V2 = [
  { key: 'discovery', title: 'Find Business' },
  { key: 'pitch', title: 'Create Pitch' },
  { key: 'offer', title: 'Build Offer' },
  { key: 'proposal', title: 'Send Proposal' },
  { key: 'close', title: 'Close & Launch' },
];

/** Convert a stage key to a 0-indexed step number for the Stepper */
export const getStepIndex = (stageKey, stages = DEAL_STAGES) => {
  const idx = stages.findIndex((s) => s.key === stageKey);
  return idx >= 0 ? idx : 0;
};

// ── Discard reasons (lead preview panel) ─────────────────────────────────────

export const DISCARD_REASONS = [
  'Not relevant',
  'Wrong market',
  'Too small',
  'Duplicate',
  'Already contacted',
  'Other',
];

// ── Snooze durations ─────────────────────────────────────────────────────────

export const SNOOZE_OPTIONS = [
  { label: 'Tomorrow', days: 1 },
  { label: 'In 3 days', days: 3 },
  { label: 'Next week', days: 7 },
];

// ── Follow-up schedule options (send proposal) ───────────────────────────────

export const FOLLOW_UP_OPTIONS = [
  { label: 'In 2 days', days: 2 },
  { label: 'In 3 days', days: 3 },
  { label: 'In 1 week', days: 7 },
  { label: 'No reminder', days: null },
];

// ── Proposal status config ───────────────────────────────────────────────────

export const PROPOSAL_STATUSES = {
  draft: { label: 'Draft', className: 'bg-slate-100 text-slate-700 border-slate-200' },
  sent: { label: 'Sent', className: 'bg-blue-100 text-blue-700 border-blue-200' },
  // Legacy compatibility: older mock/workspace data may still contain `viewed`.
  viewed: { label: 'Sent', className: 'bg-blue-100 text-blue-700 border-blue-200' },
  accepted: { label: 'Accepted', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  changes_requested: { label: 'Changes Requested', className: 'bg-orange-100 text-orange-700 border-orange-200' },
  expired: { label: 'Expired', className: 'bg-red-100 text-red-700 border-red-200' },
  lost: { label: 'Lost', className: 'bg-zinc-100 text-zinc-500 border-zinc-200' },
};

export const AGREEMENT_PAYMENT_STATUSES = {
  not_started: { label: 'Not started', className: 'bg-slate-100 text-slate-700 border-slate-200' },
  link_sent: { label: 'Billing link sent', className: 'bg-amber-100 text-amber-700 border-amber-200' },
  confirmed: { label: 'Payment method on file', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

// ── V2: Creative status config ──────────────────────────────────────────────

export const CREATIVE_STATUSES = {
  draft: { label: 'Draft', className: 'bg-slate-100 text-slate-600' },
  processing: { label: 'Generating', className: 'bg-amber-100 text-amber-700' },
  ready: { label: 'Ready', className: 'bg-blue-100 text-blue-700' },
  approved: { label: 'Approved', className: 'bg-emerald-100 text-emerald-700' },
  failed: { label: 'Failed', className: 'bg-red-100 text-red-700' },
  rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700' },
  archived: { label: 'Archived', className: 'bg-zinc-100 text-zinc-500' },
};

/** Generate a creative ID */
export const generateCreativeId = () =>
  `cr-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;

// ── V2: Offer bundles (duration-spend bundles) ──────────────────────────────

export const OFFER_BUNDLES = [
  {
    id: 'bundle-starter-2w',
    name: '2-Week Starter',
    tagline: 'Test the waters',
    description: 'A focused 2-week campaign to build local awareness.',
    duration: { weeks: 2, label: '2 weeks' },
    investmentRange: { min: 1000, max: 3000, default: 1500 },
    icon: MapPin,
    recommended: true,
    goalFit: ['awareness', 'grand_opening'],
  },
  {
    id: 'bundle-burst-2w',
    name: '2-Week Promo Burst',
    tagline: 'High-impact push',
    description: 'Short, intense burst for promotions or seasonal events.',
    duration: { weeks: 2, label: '2 weeks' },
    investmentRange: { min: 2000, max: 5000, default: 3000 },
    icon: Zap,
    recommended: false,
    goalFit: ['seasonal', 'grand_opening'],
  },
  {
    id: 'bundle-growth-4w',
    name: '4-Week Growth',
    tagline: 'Build momentum',
    description: 'Sustained presence over 4 weeks to grow your customer base.',
    duration: { weeks: 4, label: '4 weeks' },
    investmentRange: { min: 2500, max: 8000, default: 4000 },
    icon: TrendingUp,
    recommended: false,
    goalFit: ['awareness'],
  },
  {
    id: 'bundle-brand-8w',
    name: '8-Week Brand Builder',
    tagline: 'Dominate your area',
    description: 'Long-running campaign for maximum brand awareness and recall.',
    duration: { weeks: 8, label: '8 weeks' },
    investmentRange: { min: 5000, max: 15000, default: 8000 },
    icon: Building2,
    recommended: false,
    goalFit: ['awareness'],
  },
];

// ── V2: Offer scoring & rationale ────────────────────────────────────────────

// Trigger keyword → bundle bonus points (module-level, not exported)
const TRIGGER_BUNDLE_BONUSES = [
  { pattern: /first[- ]time/i, bonuses: { 'bundle-starter-2w': 25, 'bundle-burst-2w': 5 } },
  { pattern: /seasonal|push/i, bonuses: { 'bundle-burst-2w': 25, 'bundle-starter-2w': 5 } },
  { pattern: /increased.?spend|competitor.?dropped/i, bonuses: { 'bundle-brand-8w': 25, 'bundle-growth-4w': 15 } },
  { pattern: /new radio|radio campaign/i, bonuses: { 'bundle-starter-2w': 10, 'bundle-burst-2w': 10 } },
];

// Category keyword → preferred duration in weeks
const CATEGORY_DURATION_AFFINITY = [
  { pattern: /restaurant|food|bar|cafe|bakery|pizza|bbq|deli|florist|salon/i, preferWeeks: [2] },
  { pattern: /service|home|auto|repair|financial|loan|insurance|dental|medical|legal/i, preferWeeks: [4, 8] },
  { pattern: /fitness|gym|crossfit|retail|store|shop/i, preferWeeks: [2, 4] },
];

/**
 * Score each bundle for a given business context.
 * Returns a copy of OFFER_BUNDLES sorted by score (desc), with `score` and `isRecommended`.
 */
export function scoreOfferBundles(context = {}) {
  const { objective, triggerEvent, category, scopeType } = context;

  const scored = OFFER_BUNDLES.map((bundle) => {
    let score = 0;

    // 1. Goal fit (0–40 pts): objective matches bundle's goalFit
    if (objective && bundle.goalFit.includes(objective)) {
      score += 40;
    }

    // 2. Trigger match (0–25 pts): keyword regex on triggerEvent
    if (triggerEvent) {
      for (const rule of TRIGGER_BUNDLE_BONUSES) {
        if (rule.pattern.test(triggerEvent) && rule.bonuses[bundle.id]) {
          score += rule.bonuses[bundle.id];
          break; // take the first matching rule only
        }
      }
    }

    // 3. Category heuristic (0–15 pts): category → duration preference
    if (category) {
      for (const rule of CATEGORY_DURATION_AFFINITY) {
        if (rule.pattern.test(category) && rule.preferWeeks.includes(bundle.duration.weeks)) {
          score += 15;
          break;
        }
      }
    }

    // 4. Scope alignment (0–20 pts)
    if (scopeType === 'single_location') {
      if (bundle.duration.weeks <= 2) score += 20;
      else if (bundle.duration.weeks === 4) score += 10;
    } else if (scopeType === 'brand_wide') {
      if (bundle.duration.weeks >= 8) score += 20;
      else if (bundle.duration.weeks === 4) score += 15;
    } else if (scopeType === 'multiple_locations') {
      if (bundle.duration.weeks === 4) score += 20;
      else if (bundle.duration.weeks >= 8) score += 10;
    }

    return { ...bundle, score };
  });

  // Sort by score descending (stable: original order for ties)
  scored.sort((a, b) => b.score - a.score);

  // Mark highest as recommended (only if score > 0)
  const maxScore = scored[0]?.score || 0;
  return scored.map((b) => ({
    ...b,
    isRecommended: maxScore > 0 && b.score === maxScore,
  }));
}

/**
 * Generate a 1-line contextual explanation for why a bundle fits this business.
 * Returns empty string if no meaningful rationale.
 */
export function generateOfferRationale(bundle, context = {}) {
  const { objective, triggerEvent, category, scopeType, businessName } = context;
  const name = businessName || 'this business';
  const cat = category || '';

  // 1. Objective + bundle fit
  if (objective && bundle.goalFit.includes(objective)) {
    if (objective === 'grand_opening' && bundle.id.includes('burst')) {
      return `Launch ${name} with a high-impact opening campaign`;
    }
    if (objective === 'grand_opening' && bundle.id.includes('starter')) {
      return `Affordable opening campaign for ${name}`;
    }
    if (objective === 'seasonal' && bundle.id.includes('burst')) {
      return `Match their seasonal push with a focused TV burst`;
    }
    if (objective === 'awareness' && bundle.id.includes('starter')) {
      return `Perfect first campaign for ${name}`;
    }
    if (objective === 'awareness' && bundle.id.includes('brand')) {
      return `Build lasting awareness for ${name}`;
    }
    if (objective === 'awareness' && bundle.id.includes('growth')) {
      return `Sustained visibility over 4 weeks for ${name}`;
    }
  }

  // 2. Trigger + bundle fit
  if (triggerEvent) {
    if (/first[- ]time/i.test(triggerEvent) && bundle.id.includes('starter')) {
      return `Great entry point for a first-time advertiser`;
    }
    if (/seasonal|push/i.test(triggerEvent) && bundle.id.includes('burst')) {
      return `Capitalize on their seasonal radio push with TV`;
    }
    if (/competitor.?dropped/i.test(triggerEvent) && (bundle.id.includes('growth') || bundle.id.includes('brand'))) {
      return `Fill the gap left by competitors pulling back`;
    }
    if (/increased.?spend/i.test(triggerEvent) && (bundle.id.includes('growth') || bundle.id.includes('brand'))) {
      return `Match their growing radio investment with TV`;
    }
  }

  // 3. Scope + bundle fit
  if (scopeType === 'brand_wide' && bundle.id.includes('brand')) {
    return `Build awareness across all locations`;
  }
  if (scopeType === 'brand_wide' && bundle.id.includes('growth')) {
    return `Sustained reach across the whole brand`;
  }
  if (scopeType === 'single_location' && bundle.id.includes('starter')) {
    return `Focused local campaign around one location`;
  }

  // 4. Category-only
  if (cat) {
    if (/restaurant|food|bar|cafe|bakery|bbq/i.test(cat) && bundle.duration.weeks <= 2) {
      return `Short campaigns work well for ${cat.toLowerCase()}s`;
    }
    if (/service|auto|repair|dental|medical/i.test(cat) && bundle.duration.weeks >= 4) {
      return `${cat} benefits from sustained presence`;
    }
  }

  // 5. Generic fallback per bundle
  const fallbacks = {
    'bundle-starter-2w': 'A low-risk way to test CTV advertising',
    'bundle-burst-2w': 'High-impact, short-duration push',
    'bundle-growth-4w': 'Build momentum over 4 weeks',
    'bundle-brand-8w': 'Maximum exposure over 8 weeks',
  };
  return fallbacks[bundle.id] || '';
}

// ── V2: Targeting options ───────────────────────────────────────────────────

export const V2_TARGET_OPTIONS = [
  { id: 'around_location', label: 'Around this location', description: 'People near your business address', icon: MapPin },
  { id: 'custom_address', label: 'Around another address', description: 'Target a specific area', icon: Navigation },
  { id: 'city_wide', label: 'City-wide', description: 'Reach the whole city', icon: Building2 },
  { id: 'zip_codes', label: 'Specific ZIP codes', description: 'Choose exact ZIP codes', icon: Hash },
  { id: 'multi_store', label: 'Multiple store areas', description: 'Reach customers near several locations', icon: Store },
];

// ── V2: Pricing tiers + helpers ─────────────────────────────────────────────

export const PRICING_TIERS = [
  {
    id: 'tier-value',
    label: 'Value',
    markupPct: 0.20,
    description: 'Lower price point, smaller margin',
    recommended: false,
  },
  {
    id: 'tier-standard',
    label: 'Standard',
    markupPct: 0.30,
    description: 'Competitive pricing, healthy margin',
    recommended: true,
  },
  {
    id: 'tier-premium',
    label: 'Premium',
    markupPct: 0.45,
    description: 'Higher value positioning, strongest margin',
    recommended: false,
  },
];

export const DEFAULT_CPM_CAP = 28;

export const INVENTORY_TIER_GUIDE = [
  {
    id: 'efficient',
    label: 'Broader streaming mix',
    shortLabel: 'Efficiency first',
    minCpm: 16,
    maxCpm: 22,
    lowAnchor: 'Broader app mix',
    highAnchor: 'More impression volume',
    description: 'More available impression volume at a lower price point.',
  },
  {
    id: 'balanced',
    label: 'Balanced quality mix',
    shortLabel: 'Balanced',
    minCpm: 23,
    maxCpm: 32,
    lowAnchor: 'Mainstream placements',
    highAnchor: 'Stronger quality mix',
    description: 'A practical mix of scale, quality, and pacing flexibility.',
  },
  {
    id: 'premium',
    label: 'Premium streaming placements',
    shortLabel: 'Premium',
    minCpm: 33,
    maxCpm: 45,
    lowAnchor: 'Higher-demand inventory',
    highAnchor: 'Prime-time style mix',
    description: 'Higher-quality placements with tighter impression volume.',
  },
];

const clampNumber = (value, min, max) => Math.min(max, Math.max(min, value));

const formatZipLabel = (zipCodes = '') => zipCodes
  .split(',')
  .map((zip) => zip.trim())
  .filter(Boolean);

const getAddressSummary = (address = '') => {
  if (!address) return '';
  const parts = address.split(',').map((part) => part.trim()).filter(Boolean);
  if (parts.length >= 2) return `${parts[0]}, ${parts[1]}`;
  return parts[0] || address;
};

const getCitySummary = (address = '') => {
  if (!address) return '';
  const parts = address.split(',').map((part) => part.trim()).filter(Boolean);
  if (parts.length >= 2) return parts[1];
  return parts[0] || '';
};

export const getInventoryTierForCpm = (cpm = DEFAULT_CPM_CAP) => {
  const normalized = Number(cpm) || DEFAULT_CPM_CAP;
  return INVENTORY_TIER_GUIDE.find((tier) => normalized >= tier.minCpm && normalized <= tier.maxCpm)
    || INVENTORY_TIER_GUIDE[1];
};

export const buildTargetAreaSummary = ({
  targetMode = 'standard_area',
  center = '',
  radius = 10,
  zipCodes = '',
  businessName = '',
} = {}) => {
  const addressSummary = getAddressSummary(center);
  const citySummary = getCitySummary(center);
  const zipList = formatZipLabel(zipCodes);

  switch (targetMode) {
    case 'hyper_local':
      return {
        label: addressSummary
          ? `Hyper-local around ${addressSummary}`
          : (businessName ? `Hyper-local around ${businessName}` : 'Hyper-local area'),
        helperText: 'Hyper-local targeting narrows the available audience and can limit delivery.',
      };
    case 'radius':
      return {
        label: addressSummary
          ? `${radius}-mile radius around ${addressSummary}`
          : `${radius}-mile radius`,
        helperText: 'Radius targeting gives more control when you need to shape a custom local area.',
      };
    case 'zip_codes':
      return {
        label: zipList.length
          ? `ZIP code targeting: ${zipList.join(', ')}`
          : 'ZIP code targeting',
        helperText: 'ZIP targeting is best when the seller already knows the exact delivery map.',
      };
    case 'standard_area':
    default:
      return {
        label: citySummary
          ? `Standard area across ${citySummary}`
          : addressSummary
            ? `Standard area around ${addressSummary}`
            : (businessName ? `Standard area around ${businessName}` : 'Standard market area'),
        helperText: 'Standard targeting keeps reach broader and pacing easier to maintain.',
      };
  }
};

export const estimateReachFromOffer = ({
  budget = 0,
  cpmCap = DEFAULT_CPM_CAP,
  targetMode = 'standard_area',
  radius = 10,
  zipCodes = '',
  flightWeeks = 2,
} = {}) => {
  const safeBudget = Math.max(0, Number(budget) || 0);
  const safeCpm = clampNumber(Number(cpmCap) || DEFAULT_CPM_CAP, 1, 1000);
  const safeRadius = clampNumber(Number(radius) || 10, 1, 50);
  const safeWeeks = Math.max(1, Number(flightWeeks) || 1);
  const zipCount = formatZipLabel(zipCodes).length;
  const maxImpressions = Math.round((safeBudget / safeCpm) * 1000);

  const frequencyByMode = {
    hyper_local: 4.4,
    radius: 3.8,
    zip_codes: 3.6,
    standard_area: 3.1,
  };
  const audienceCapByMode = {
    hyper_local: Math.max(6000, Math.round(safeRadius * 2400)),
    radius: Math.max(18000, Math.round(safeRadius * 6400)),
    zip_codes: Math.max(12000, zipCount * 14000),
    standard_area: Math.max(40000, Math.round(safeRadius * 9500)),
  };
  const pacingMultiplier = safeWeeks >= 8 ? 0.88 : safeWeeks >= 4 ? 0.76 : 0.64;
  const avgFrequency = frequencyByMode[targetMode] || frequencyByMode.standard_area;
  const addressableHouseholds = audienceCapByMode[targetMode] || audienceCapByMode.standard_area;
  const estimatedHouseholds = Math.min(addressableHouseholds, Math.round(maxImpressions / avgFrequency));
  const estimatedUniqueReach = Math.round(estimatedHouseholds * pacingMultiplier);

  return {
    maxImpressions,
    estimatedHouseholds,
    estimatedUniqueReach,
    dailyImpressions: Math.round(maxImpressions / Math.max(1, safeWeeks * 7)),
    note: targetMode === 'hyper_local'
      ? 'Targeting only the immediate neighborhood means fewer total people see the ad, but the people who do are nearby.'
      : targetMode === 'zip_codes'
        ? 'ZIP targeting is precise. Total reach depends on the ZIPs you picked.'
        : 'These estimates assume normal delivery for the ad-view price you set.',
  };
};

/**
 * Compute pricing breakdown from investment and markup tier.
 *
 * Model: investment = platform cost (what it costs to run the campaign).
 * Sell price = investment marked up by the tier percentage.
 * Seller keeps the difference.
 */
export const computePricing = (investment, tier) => {
  const platformCost = investment;
  const sellPrice = Math.round(platformCost / (1 - tier.markupPct));
  const sellerEarnings = sellPrice - platformCost;
  const marginPct = sellPrice > 0 ? sellerEarnings / sellPrice : 0;
  return { platformCost, sellPrice, sellerEarnings, marginPct, markupPct: tier.markupPct };
};

/** Margin health indicator */
export const getMarginHealth = (marginPct) => {
  if (marginPct >= 0.35) return { label: 'Strong', color: 'emerald' };
  if (marginPct >= 0.25) return { label: 'Healthy', color: 'teal' };
  if (marginPct >= 0.15) return { label: 'Tight', color: 'amber' };
  return { label: 'Low', color: 'red' };
};

// ── V2: Mock proposals per business ─────────────────────────────────────────

export const MOCK_PROPOSALS = {
  'biz-kfire': [
    {
      id: 'prop-kfire-1',
      business_profile_id: 'biz-kfire',
      creativeId: 'cr-kfire-1',
      packageName: '2-Week Starter',
      budget_cap: 1500,
      price: 2143,
      flight: { start: '2026-02-15', end: '2026-03-01', weeks: 2 },
      target_area: { type: 'around_location', label: 'Around KFire Korean BBQ' },
      status: 'accepted',
      pitch_message: 'CTV campaign proposal for KFire Korean BBQ.',
      created_at: '2026-02-14T10:00:00Z',
      pricing: { sellPrice: 2143, platformCost: 1500, sellerEarnings: 643, marginPct: 0.30, tierId: 'tier-standard' },
    },
    {
      id: 'prop-kfire-2',
      business_profile_id: 'biz-kfire',
      creativeId: 'cr-kfire-1',
      packageName: '4-Week Growth',
      budget_cap: 4000,
      price: 5714,
      flight: { start: '2026-03-15', end: '2026-04-12', weeks: 4 },
      target_area: { type: 'around_location', label: 'Around KFire Korean BBQ' },
      status: 'sent',
      pitch_message: 'Follow-up growth campaign for KFire.',
      created_at: '2026-03-05T14:00:00Z',
      pricing: { sellPrice: 5714, platformCost: 4000, sellerEarnings: 1714, marginPct: 0.30, tierId: 'tier-standard' },
    },
  ],
  'biz-bloom': [
    {
      id: 'prop-bloom-1',
      business_profile_id: 'biz-bloom',
      creativeId: 'cr-bloom-1',
      packageName: '2-Week Promo Burst',
      budget_cap: 3000,
      price: 4286,
      flight: { start: '2026-03-01', end: '2026-03-15', weeks: 2 },
      target_area: { type: 'around_location', label: 'Around Bloom & Petal' },
      status: 'draft',
      pitch_message: 'Valentine\'s promo campaign for Bloom & Petal.',
      created_at: '2026-03-01T09:00:00Z',
      pricing: { sellPrice: 4286, platformCost: 3000, sellerEarnings: 1286, marginPct: 0.30, tierId: 'tier-standard' },
    },
  ],
  'biz-hearth': [
    {
      id: 'prop-hearth-1',
      business_profile_id: 'biz-hearth',
      creative_set_id: 'cs-hearth-1',
      creativeId: 'cr-hearth-hero',
      package_id: 'bundle-burst-2w',
      packageName: '2-Week Dinner Service Push',
      budget_cap: 3500,
      price: 5000,
      flight: { start: '2026-03-01', end: '2026-03-15', weeks: 2 },
      target_area: { type: 'around_location', label: '3 miles around LoHi and Platte Street' },
      status: 'accepted',
      version: 1,
      previousVersionId: null,
      followUpDays: 3,
      contact: 'Avery Quinn',
      deliveryMethod: 'email',
      recipientEmail: 'hello@hearthdenver.com',
      pitch_message: 'Launch a premium dinner-service awareness campaign for Hearth across Denver streaming households.',
      created_at: '2026-02-27T16:00:00Z',
      updated_at: '2026-02-28T18:00:00Z',
      sent_at: '2026-02-27T18:30:00Z',
      viewed_at: '2026-02-28T09:15:00Z',
      accepted_at: '2026-02-28T18:00:00Z',
      changes_requested_at: null,
      expired_at: null,
      lost_at: null,
      pricing: { sellPrice: 5000, platformCost: 3500, sellerEarnings: 1500, marginPct: 0.30, tierId: 'tier-standard' },
    },
    {
      id: 'prop-hearth-2',
      business_profile_id: 'biz-hearth',
      creative_set_id: 'cs-hearth-2',
      creativeId: 'cr-hearth-brunch',
      package_id: 'bundle-growth-4w',
      packageName: '4-Week Weekend Brunch Expansion',
      budget_cap: 6000,
      price: 8571,
      flight: { start: '2026-03-22', end: '2026-04-19', weeks: 4 },
      target_area: { type: 'city_wide', label: 'Denver food and dining audience' },
      status: 'sent',
      version: 2,
      previousVersionId: 'prop-hearth-1',
      followUpDays: 2,
      contact: 'Avery Quinn',
      deliveryMethod: 'email',
      recipientEmail: 'hello@hearthdenver.com',
      pitch_message: 'Follow-up expansion proposal focused on weekend brunch growth and private dining bookings.',
      created_at: '2026-03-08T11:00:00Z',
      updated_at: '2026-03-10T15:00:00Z',
      sent_at: '2026-03-09T09:00:00Z',
      viewed_at: null,
      accepted_at: null,
      changes_requested_at: null,
      expired_at: null,
      lost_at: null,
      pricing: { sellPrice: 8571, platformCost: 6000, sellerEarnings: 2571, marginPct: 0.30, tierId: 'tier-standard' },
    },
  ],
};

/** Generate a proposal ID */
export const generateProposalId = () =>
  `prop-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;

// ── Helpers ──────────────────────────────────────────────────────────────────

export const generateDealId = () =>
  `deal-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;

/** Format budget in dollars */
export const formatBudget = (cents) => {
  if (!cents && cents !== 0) return '';
  return `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

/** Format dollars (already in dollars, not cents) */
export const formatDollars = (amount) => {
  if (!amount && amount !== 0) return '';
  return `$${Number(amount).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

/** Compute flight end date from start + weeks */
export const computeFlightEnd = (startDate, weeks) => {
  const end = new Date(startDate);
  end.setDate(end.getDate() + weeks * 7);
  return end.toISOString().split('T')[0];
};

/** Generate a mock creative asset after "generation" */
export const generateMockCreativeSet = (businessProfile, objective, tone, offer, format) => ({
  id: `cs-${Date.now()}`,
  business_profile_id: businessProfile?.id,
  status: 'ready',
  objective,
  tone,
  offer: offer || null,
  format: format || 'ctv_16_9',
  assets: [
    {
      type: 'video',
      url: null, // placeholder — no real video
      thumbnail_url: `https://placehold.co/640x360/FF6B6B/white?text=${encodeURIComponent(businessProfile?.name || 'Spec Ad')}`,
      label: '30s CTV Spot',
    },
  ],
  share_url: `https://cornercast.tv/preview/cs-${Date.now()}`,
});

/** Generate a mock proposal from deal state */
export const isMockGenerationEnabled = () => {
  if (import.meta.env.VITE_ENABLE_MOCK_GENERATION === 'true') return true;
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    if (params.get('mockGeneration') === 'true') return true;
  }
  return false;
};

export const isAuthDisabled = () => import.meta.env.VITE_AUTH_DISABLED === 'true';

export const getLatestProposalForBusiness = (proposals = []) => {
  if (!proposals.length) return null;
  return [...proposals]
    .filter((proposal) => proposal.status !== 'deleted')
    .sort((a, b) => {
      const aTs = new Date(a.updated_at || a.created_at || 0).getTime();
      const bTs = new Date(b.updated_at || b.created_at || 0).getTime();
      return bTs - aTs;
    })[0] || null;
};

export const upsertProposalForBusiness = (proposalsByBusiness, businessId, proposal) => {
  const existing = proposalsByBusiness[businessId] || [];
  const index = existing.findIndex((item) => item.id === proposal.id);
  const next = [...existing];
  if (index >= 0) next[index] = { ...next[index], ...proposal };
  else next.push(proposal);
  next.sort((a, b) => new Date(b.updated_at || b.created_at || 0).getTime() - new Date(a.updated_at || a.created_at || 0).getTime());
  return {
    ...proposalsByBusiness,
    [businessId]: next,
  };
};

export const getCurrentCreativeForBusiness = (dealState, businessId, envFlags = {}) => {
  const businessCreatives = dealState.creatives?.[businessId] || [];
  const selectedCreative = businessCreatives.find((creative) => creative.id === dealState.selectedCreativeId);
  const latestLibraryCreative = [...businessCreatives]
    .filter((creative) => creative.status !== 'archived')
    .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())[0];
  const getCreativeLabel = (creative) =>
    creative?.displayName
    || creative?.creativeName
    || creative?.creative_name
    || creative?.objective
    || 'Creative';

  if (selectedCreative) {
    const detail = selectedCreative.status === 'approved'
      ? 'Creative approved.'
      : selectedCreative.status === 'processing'
        ? 'Creative generation is still in progress.'
        : selectedCreative.errorDetail
          ? `Creative generation failed: ${selectedCreative.errorDetail}`
          : `Creative status: ${selectedCreative.status || 'draft'}.`;
    return {
      type: 'library',
      label: getCreativeLabel(selectedCreative),
      thumbnailUrl: selectedCreative.thumbnailUrl || '',
      creative: selectedCreative,
      ready: ['ready', 'approved'].includes(selectedCreative.status),
      detail,
      isMock: false,
    };
  }

  if (dealState.creativeSet?.assets?.[0]) {
    return {
      type: 'session',
      label: dealState.creativeSet.assets[0].label || 'Creative preview',
      thumbnailUrl: dealState.creativeSet.assets[0].thumbnail_url || '',
      creative: dealState.creativeSet,
      ready: ['ready', 'approved'].includes(dealState.creativeSet.status),
      detail: dealState.creativeSet.status === 'approved'
        ? 'Generated creative approved.'
        : `Generated creative status: ${dealState.creativeSet.status || 'ready'}.`,
      isMock: false,
    };
  }

  if (latestLibraryCreative) {
    const detail = latestLibraryCreative.status === 'approved'
      ? 'Creative approved.'
      : latestLibraryCreative.status === 'processing'
        ? 'Creative generation is still in progress.'
        : latestLibraryCreative.errorDetail
          ? `Creative generation failed: ${latestLibraryCreative.errorDetail}`
          : `Creative status: ${latestLibraryCreative.status || 'draft'}.`;
    return {
      type: 'library',
      label: getCreativeLabel(latestLibraryCreative),
      thumbnailUrl: latestLibraryCreative.thumbnailUrl || '',
      creative: latestLibraryCreative,
      ready: ['ready', 'approved'].includes(latestLibraryCreative.status),
      detail,
      isMock: false,
    };
  }

  if (envFlags.mockGenerationEnabled) {
    const businessName = dealState.businessProfile?.name || dealState.businesses?.[businessId]?.name || 'Creative Preview';
    return {
      type: 'mock',
      label: 'Mock creative preview',
      thumbnailUrl: `https://placehold.co/960x540/E9EEF3/1E293B?text=${encodeURIComponent(businessName)}`,
      creative: null,
      ready: true,
      detail: 'Mock generation enabled for workflow testing.',
      isMock: true,
    };
  }

  return {
    type: 'none',
    label: 'No creative',
    thumbnailUrl: '',
    creative: null,
    ready: false,
    detail: 'No creative asset is attached yet.',
    isMock: false,
  };
};

export const generateMockProposal = (dealState, options = {}) => {
  const now = new Date().toISOString();
  const businessId = dealState.activeBusinessId || dealState.businessProfile?.id;
  const latest = getLatestProposalForBusiness(dealState.proposals?.[businessId] || []);
  const creative = getCurrentCreativeForBusiness(dealState, businessId, { mockGenerationEnabled: !!options.mockGenerationEnabled });
  const defaultContact = dealState.scope?.contact || latest?.contact || {};
  const budget = dealState.selectedPackage?.budget;
  const flightWeeks = dealState.selectedPackage?.duration?.weeks || dealState.selectedPackage?.flight || 2;

  return {
    id: options.id || `prop-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
    business_profile_id: businessId,
    creative_set_id: dealState.creativeSet?.id || null,
    creativeId: dealState.selectedCreativeId || latest?.creativeId || creative.creative?.id || null,
    package_id: dealState.selectedPackage?.id || latest?.package_id || null,
    packageName: dealState.selectedPackage?.name || latest?.packageName || 'Campaign Proposal',
    budget_cap: budget || latest?.budget_cap || 0,
    flight: {
      start: new Date().toISOString().split('T')[0],
      end: computeFlightEnd(new Date(), flightWeeks),
      weeks: flightWeeks,
      label: dealState.selectedPackage?.duration?.label || `${flightWeeks} weeks`,
    },
    target_area: {
      type: dealState.selectedPackage?.targetArea || latest?.target_area?.type || 'around_location',
      label: dealState.selectedPackage?.targetAddress || latest?.target_area?.label || (dealState.businessProfile?.name ? `Around ${dealState.businessProfile.name}` : 'Not set'),
    },
    price: dealState.selectedPackage?.pricing?.sellPrice || budget || latest?.price || 0,
    pricing: dealState.selectedPackage?.pricing || latest?.pricing || null,
    status: options.status || 'draft',
    pitch_message: dealState.creativeSet || creative.ready
      ? `Hi there,\n\nI put together a CornerCast streaming TV proposal for ${dealState.businessProfile?.name || 'your business'}. You can review the creative concept, targeting, and investment summary below.\n\nIf this aligns with your goals, I can finalize launch and billing right away.`
      : `Hi there,\n\nI put together a CornerCast streaming TV proposal for ${dealState.businessProfile?.name || 'your business'}. You can review the recommended targeting and investment summary below.\n\nIf this aligns with your goals, I can finalize creative and launch details right away.`,
    contact: {
      name: defaultContact.name || '',
      email: defaultContact.email || '',
    },
    deliveryMethod: latest?.deliveryMethod || 'link',
    recipientEmail: latest?.recipientEmail || defaultContact.email || '',
    created_at: options.created_at || now,
    updated_at: options.updated_at || now,
    sent_at: options.sent_at || null,
    viewed_at: options.viewed_at || null,
    accepted_at: options.accepted_at || null,
    changes_requested_at: options.changes_requested_at || null,
    expired_at: options.expired_at || null,
    lost_at: options.lost_at || null,
    version: options.version || 1,
    previousVersionId: options.previousVersionId || null,
    followUpDays: options.followUpDays ?? latest?.followUpDays ?? null,
  };
};

export const getProposalTimeline = (proposal, agreement) => {
  if (!proposal) return [];
  const events = [
    proposal.created_at ? { key: 'created', label: 'Draft created', timestamp: proposal.created_at } : null,
    proposal.sent_at ? { key: 'sent', label: 'Proposal sent', timestamp: proposal.sent_at } : null,
    proposal.changes_requested_at ? { key: 'changes', label: 'Changes requested', timestamp: proposal.changes_requested_at } : null,
    proposal.accepted_at ? { key: 'accepted', label: 'Proposal accepted', timestamp: proposal.accepted_at } : null,
    proposal.lost_at ? { key: 'lost', label: 'Proposal marked lost', timestamp: proposal.lost_at } : null,
    proposal.expired_at ? { key: 'expired', label: 'Proposal expired', timestamp: proposal.expired_at } : null,
    agreement?.billing_link_sent_at ? { key: 'billing_link', label: 'Billing link sent', timestamp: agreement.billing_link_sent_at } : null,
    agreement?.payment_confirmed_at ? { key: 'payment', label: 'Payment method confirmed', timestamp: agreement.payment_confirmed_at } : null,
  ].filter(Boolean);
  return events.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
};

export const getLaunchBlockers = (dealState, businessId, envFlags = {}) => {
  const proposal = dealState.proposal || getLatestProposalForBusiness(dealState.proposals?.[businessId] || []);
  const agreement = dealState.agreement || null;
  const creative = getCurrentCreativeForBusiness(dealState, businessId, envFlags);
  const contact = proposal?.contact || dealState.scope?.contact || {};
  const targetAddress = proposal?.target_area?.label || dealState.selectedPackage?.targetAddress || '';
  const flightWeeks = proposal?.flight?.weeks || dealState.selectedPackage?.duration?.weeks || dealState.selectedPackage?.flight || 0;

  const items = [
    {
      key: 'proposal',
      label: 'Proposal accepted',
      resolveLabel: 'Open proposal',
      resolvePath: `/proposal`,
      ready: proposal?.status === 'accepted',
      detail: proposal?.status
        ? `Current status: ${PROPOSAL_STATUSES[proposal.status]?.label || proposal.status}.`
        : 'No proposal exists for this business.',
      severity: 'high',
    },
    {
      key: 'billing',
      label: 'Billing confirmed',
      resolveLabel: 'Open billing',
      resolvePath: `/close`,
      ready: agreement?.payment_method_status === 'confirmed',
      detail: agreement?.payment_method_status === 'link_sent'
        ? 'Billing link sent. Waiting for buyer to add payment method.'
        : agreement?.payment_method_status === 'confirmed'
          ? 'Payment method is on file.'
          : 'No billing setup completed yet.',
      severity: 'high',
    },
    {
      key: 'targeting',
      label: 'Targeting confirmed',
      resolveLabel: 'Open offer',
      resolvePath: `/offer`,
      ready: !!((proposal?.target_area?.type || dealState.selectedPackage?.targetArea) && targetAddress),
      detail: targetAddress || 'No target area selected.',
      severity: 'medium',
    },
    {
      key: 'flight',
      label: 'Flight configured',
      resolveLabel: 'Open offer',
      resolvePath: `/offer`,
      ready: flightWeeks > 0,
      detail: flightWeeks > 0 ? `${flightWeeks} week flight configured.` : 'Flight duration is missing.',
      severity: 'medium',
    },
    {
      key: 'creative',
      label: 'Creative ready for launch',
      resolveLabel: 'Open creative',
      resolvePath: `/pitch`,
      ready: creative.ready,
      detail: creative.detail,
      severity: 'medium',
    },
    {
      key: 'contact',
      label: 'Buyer contact recorded',
      resolveLabel: 'Open proposal',
      resolvePath: `/proposal`,
      ready: proposal?.deliveryMethod !== 'email' || !!contact.email,
      detail: proposal?.deliveryMethod === 'email'
        ? (contact.email ? `Proposal recipient: ${contact.email}` : 'Email delivery selected but no recipient email is stored.')
        : 'Contact email is optional for link delivery.',
      severity: 'low',
    },
  ];

  const blockers = items.filter((item) => !item.ready);
  const readyItems = items.filter((item) => item.ready);
  return {
    blockers,
    readyItems,
    items,
    allReady: blockers.length === 0,
    progressPct: Math.round((readyItems.length / items.length) * 100),
    envFlags,
  };
};

// ── V2: Mock businesses ─────────────────────────────────────────────────────

export const MOCK_BUSINESSES = [
  {
    id: 'biz-kfire',
    name: 'KFire Korean BBQ',
    address: '2535 N California Ave, Chicago, IL 60647',
    category: 'Korean Restaurant',
    scope: 'single_location',
    rating: 4.6,
    reviews: 342,
    website_url: 'https://kfirebbq.com',
    createdAt: '2026-03-01T10:00:00Z',
  },
  {
    id: 'biz-bloom',
    name: 'Bloom & Petal Florist',
    address: '1847 W Division St, Chicago, IL 60622',
    category: 'Florist',
    scope: 'single_location',
    rating: 4.8,
    reviews: 128,
    website_url: 'https://bloomandpetal.com',
    createdAt: '2026-02-28T14:30:00Z',
  },
  {
    id: 'biz-autohaus',
    name: 'AutoHaus Chicago',
    address: '3200 N Western Ave, Chicago, IL 60618',
    category: 'Auto Repair',
    scope: 'brand_wide',
    rating: 4.3,
    reviews: 89,
    website_url: 'https://autohauschicago.com',
    createdAt: '2026-02-25T09:15:00Z',
  },
  {
    id: 'biz-hearth',
    name: STUDIO_HEARTH_BUSINESS.name,
    address: STUDIO_HEARTH_BUSINESS.address,
    category: STUDIO_HEARTH_BUSINESS.category,
    scope: 'single_location',
    rating: STUDIO_HEARTH_BUSINESS.rating,
    reviews: STUDIO_HEARTH_BUSINESS.reviews_count,
    website_url: STUDIO_HEARTH_BUSINESS.website,
    createdAt: '2026-02-26T15:00:00Z',
  },
];

/** Generate a business ID */
export const generateBusinessId = () =>
  `biz-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;

// ── V2 Phase 5: Pipeline + commercial signal helpers ─────────────────────────

/** Estimate deal value from lead data (mock heuristic) */
export const estimateDealValue = (lead) => {
  if (lead.status === 'closed' && lead.closedReason === 'won') {
    return lead.dealValue || '$2,500';
  }
  if (lead.mediaPlans?.length > 0) {
    return lead.mediaPlans[0].budget || '$2,500';
  }
  if (lead.businessRating >= 4.8) return '$4,000';
  if (lead.businessRating >= 4.5) return '$2,500';
  if (lead.businessRating >= 4.0) return '$1,500';
  return '$1,000';
};

/** Temperature indicator based on recency and engagement */
export const getTemperature = (lead) => {
  if (lead.status === 'closed') return null;
  const hoursSinceTrigger = (Date.now() - new Date(lead.triggerDate).getTime()) / (1000 * 60 * 60);
  if (lead.status === 'active' && lead.mediaPlans?.length > 0) return 'hot';
  if (hoursSinceTrigger < 48) return 'hot';
  if (hoursSinceTrigger < 120) return 'warm';
  return 'cold';
};

export const TEMPERATURE_CONFIG = {
  hot: { label: 'Hot', className: 'bg-red-100 text-red-700 border-red-200' },
  warm: { label: 'Warm', className: 'bg-amber-100 text-amber-700 border-amber-200' },
  cold: { label: 'Cold', className: 'bg-blue-100 text-blue-700 border-blue-200' },
};

/** Workspace status line for a lead */
export const getWorkspaceStatus = (lead, businesses, creatives, proposals) => {
  const bizId = lead.businessProfileId;
  const bProposals = proposals?.[bizId] || [];
  const bCreatives = creatives?.[bizId] || [];

  if (bProposals.some((p) => p.status === 'accepted')) return 'Deal closed';
  if (bProposals.some((p) => p.status === 'sent')) return 'Proposal sent';
  if (bProposals.some((p) => p.status === 'draft')) return 'Proposal drafted';
  if (bCreatives.length > 0) return 'Creative ready';
  if (businesses?.[bizId]) return 'Business added';
  return null;
};

/** Compute pipeline summary from leads */
export const computePipelineSummary = (leads) => {
  let totalValue = 0;
  let activeDeals = 0;
  let closedWon = 0;
  leads.forEach((lead) => {
    if (lead.status === 'closed' && lead.closedReason === 'won') closedWon++;
    if (lead.status !== 'closed') {
      activeDeals++;
      const valStr = estimateDealValue(lead).replace(/[^0-9]/g, '');
      totalValue += parseInt(valStr, 10) || 0;
    }
  });
  return { totalValue, activeDeals, closedWon };
};

// ── V2 Phase 5: Mock activity events per business ────────────────────────────

const KFIRE_ACTIVITY = [
  { id: 'act-1', type: 'proposal_accepted', label: 'Proposal accepted', detail: '2-Week Starter — $2,143', timestamp: '2026-03-06T14:00:00Z' },
  { id: 'act-2', type: 'proposal_sent', label: 'Proposal sent', detail: '2-Week Starter', timestamp: '2026-03-05T10:00:00Z' },
  { id: 'act-3', type: 'creative_approved', label: 'Creative approved', detail: '30s CTV Spot — Awareness', timestamp: '2026-03-04T16:00:00Z' },
  { id: 'act-4', type: 'creative_generated', label: 'Creative generated', detail: 'Spec ad for KFire Korean BBQ', timestamp: '2026-03-04T12:00:00Z' },
  { id: 'act-5', type: 'business_added', label: 'Business added', detail: 'From lead: New radio campaign on WBBM-FM', timestamp: '2026-03-04T10:00:00Z' },
];

const HEARTH_ACTIVITY = [
  { id: 'act-hearth-2', type: 'proposal_sent', label: 'Proposal sent', detail: '4-Week Weekend Brunch Expansion — $8,571', timestamp: '2026-03-09T09:00:00Z' },
  { id: 'act-hearth-3', type: 'campaign_live', label: 'Campaign launched', detail: '2-Week Dinner Service Push', timestamp: '2026-03-01T15:00:00Z' },
  { id: 'act-hearth-4', type: 'proposal_accepted', label: 'Proposal accepted', detail: '2-Week Dinner Service Push — $5,000', timestamp: '2026-02-28T18:00:00Z' },
  { id: 'act-hearth-5', type: 'creative_approved', label: 'Creative approved', detail: '30s CTV spot with chef-led dinner footage', timestamp: '2026-02-27T16:30:00Z' },
  { id: 'act-hearth-6', type: 'business_added', label: 'Business added', detail: 'Demo workspace with seeded creative, proposal, and campaign data', timestamp: '2026-02-26T15:00:00Z' },
];

const BLOOM_ACTIVITY = [
  { id: 'act-6', type: 'proposal_drafted', label: 'Proposal drafted', detail: '2-Week Promo Burst — $4,286', timestamp: '2026-03-01T09:00:00Z' },
  { id: 'act-7', type: 'creative_generated', label: 'Creative generated', detail: "Valentine's promo spec ad", timestamp: '2026-02-28T15:00:00Z' },
  { id: 'act-8', type: 'business_added', label: 'Business added', detail: 'Manually added', timestamp: '2026-02-28T14:30:00Z' },
];

const AUTOHAUS_ACTIVITY = [
  { id: 'act-9', type: 'business_added', label: 'Business added', detail: 'Manually added', timestamp: '2026-02-25T09:15:00Z' },
];

export const MOCK_ACTIVITY = {
  'biz-kfire': KFIRE_ACTIVITY,
  'bp-kfire-001': KFIRE_ACTIVITY,
  'biz-bloom': BLOOM_ACTIVITY,
  'biz-autohaus': AUTOHAUS_ACTIVITY,
  'biz-hearth': HEARTH_ACTIVITY,
};

// ── V2 Phase 5: Mock campaigns per business ──────────────────────────────────

const KFIRE_CAMPAIGNS = [
  {
    id: 'camp-kfire-1',
    name: '2-Week Starter',
    status: 'live',
    spend: 1247,
    budget_cap: 2500,
    households_reached: 8420,
    impressions: 24650,
    start_date: '2026-03-01',
    end_date: '2026-03-15',
    days_remaining: 9,
  },
];

const KFIRE_THUMBNAIL_URL = '/.mock/7189ad10-f4d2-49a7-8629-452ab1fc9f2a.jpg';

const HEARTH_CAMPAIGNS = [
  {
    id: 'camp-hearth-1',
    name: '2-Week Dinner Service Push',
    status: 'live',
    spend: 2175,
    budget_cap: 3500,
    households_reached: 14320,
    impressions: 42680,
    start_date: '2026-03-01',
    end_date: '2026-03-15',
    days_remaining: 4,
    launched_at: '2026-03-01T15:00:00Z',
    proposalVersion: 1,
  },
];

export const MOCK_CAMPAIGNS = {
  'biz-kfire': KFIRE_CAMPAIGNS,
  'bp-kfire-001': KFIRE_CAMPAIGNS,
  'biz-hearth': HEARTH_CAMPAIGNS,
};

const MOCK_BUSINESS_SEEDS = {
  'biz-kfire': {
    business: {
      id: 'biz-kfire',
      name: 'KFire Korean BBQ',
      address: '2528 N Milwaukee Ave, Chicago, IL 60647',
      category: 'Korean Restaurant',
      scope: 'single_location',
      rating: 4.6,
      reviews: 342,
      website_url: 'https://kfirebbq.com',
      createdAt: '2026-03-01T10:00:00Z',
    },
    businessProfile: {
      id: 'biz-kfire',
      place_id: 'mock-place-kfire-001',
      website_url: 'https://kfirebbq.com',
      name: 'KFire Korean BBQ',
      address: '2528 N Milwaukee Ave, Chicago, IL 60647',
      location_geo: null,
      category: 'Korean Restaurant',
      photos: [KFIRE_THUMBNAIL_URL],
      logo_url: null,
      reviews: 342,
      rating: 4.6,
      phone: '(773) 555-2528',
    },
    creatives: [
      {
        id: 'cr-kfire-hero',
        businessId: 'biz-kfire',
        status: 'approved',
        objective: 'awareness',
        tone: 'bold',
        scope: 'single_location',
        format: 'ctv_16_9',
        duration: 30,
        thumbnailUrl: KFIRE_THUMBNAIL_URL,
        shareUrl: 'https://cornercast.tv/preview/cr-kfire-hero',
        assets: [
          {
            type: 'video',
            url: '/.mock/b6977756-5243-4029-880f-c0f380b4a626.mp4',
            thumbnail_url: KFIRE_THUMBNAIL_URL,
            label: '30s Hero Spot',
          },
        ],
        proposalIds: ['prop-kfire-live', 'prop-kfire-followup'],
        createdAt: '2026-03-04T12:00:00Z',
      },
      {
        id: 'cr-kfire-sizzle',
        businessId: 'biz-kfire',
        status: 'approved',
        objective: 'awareness',
        tone: 'energetic',
        scope: 'single_location',
        format: 'ctv_16_9',
        duration: 15,
        thumbnailUrl: KFIRE_THUMBNAIL_URL,
        shareUrl: 'https://cornercast.tv/preview/cr-kfire-sizzle',
        assets: [
          {
            type: 'video',
            url: '/.mock/de248bc0-0a23-4975-aa4c-b978549e204a.mp4',
            thumbnail_url: KFIRE_THUMBNAIL_URL,
            label: '15s Sizzle Spot',
          },
        ],
        proposalIds: ['prop-kfire-followup'],
        createdAt: '2026-03-04T13:00:00Z',
      },
      {
        id: 'cr-kfire-lunch',
        businessId: 'biz-kfire',
        status: 'ready',
        objective: 'seasonal',
        tone: 'friendly',
        scope: 'single_location',
        format: 'ctv_16_9',
        duration: 15,
        thumbnailUrl: KFIRE_THUMBNAIL_URL,
        shareUrl: 'https://cornercast.tv/preview/cr-kfire-lunch',
        assets: [
          {
            type: 'video',
            url: '/.mock/7189ad10-f4d2-49a7-8629-452ab1fc9f2a.mp4',
            thumbnail_url: KFIRE_THUMBNAIL_URL,
            label: '15s Lunch Promo',
          },
        ],
        proposalIds: [],
        createdAt: '2026-03-05T09:30:00Z',
      },
    ],
    proposals: [
      {
        id: 'prop-kfire-live',
        business_profile_id: 'biz-kfire',
        creative_set_id: 'cs-kfire-hero',
        creativeId: 'cr-kfire-hero',
        package_id: 'bundle-starter-2w',
        packageName: '2-Week Starter',
        budget_cap: 2500,
        price: 3571,
        flight: { start: '2026-03-01', end: '2026-03-15', weeks: 2 },
        target_area: { type: 'around_location', label: '2 miles around Logan Square' },
        status: 'accepted',
        version: 1,
        previousVersionId: null,
        followUpDays: 3,
        contact: 'Min Park',
        deliveryMethod: 'email',
        recipientEmail: 'marketing@kfirebbq.com',
        pitch_message: 'Drive dinner traffic and protect KFire share of voice while radio activity is active in-market.',
        created_at: '2026-02-27T11:00:00Z',
        updated_at: '2026-02-28T16:00:00Z',
        sent_at: '2026-02-27T13:00:00Z',
        viewed_at: '2026-02-28T09:30:00Z',
        accepted_at: '2026-02-28T16:00:00Z',
        changes_requested_at: null,
        expired_at: null,
        lost_at: null,
        pricing: { sellPrice: 3571, platformCost: 2500, sellerEarnings: 1071, marginPct: 0.30, tierId: 'tier-standard' },
      },
      {
        id: 'prop-kfire-followup',
        business_profile_id: 'biz-kfire',
        creative_set_id: 'cs-kfire-sizzle',
        creativeId: 'cr-kfire-sizzle',
        package_id: 'bundle-growth-4w',
        packageName: '4-Week Growth',
        budget_cap: 5000,
        price: 7143,
        flight: { start: '2026-03-20', end: '2026-04-17', weeks: 4 },
        target_area: { type: 'city_wide', label: 'Chicago food and dining audience' },
        status: 'sent',
        version: 2,
        previousVersionId: 'prop-kfire-live',
        followUpDays: 2,
        contact: 'Min Park',
        deliveryMethod: 'email',
        recipientEmail: 'marketing@kfirebbq.com',
        pitch_message: 'Scale KFire from a local awareness test into a broader Chicago dining push anchored by premium streaming inventory.',
        created_at: '2026-03-05T14:00:00Z',
        updated_at: '2026-03-06T10:30:00Z',
        sent_at: '2026-03-06T10:30:00Z',
        viewed_at: null,
        accepted_at: null,
        changes_requested_at: null,
        expired_at: null,
        lost_at: null,
        pricing: { sellPrice: 7143, platformCost: 5000, sellerEarnings: 2143, marginPct: 0.30, tierId: 'tier-standard' },
      },
    ],
    agreement: {
      id: 'agr-kfire-1',
      proposalId: 'prop-kfire-live',
      accepted_at: '2026-02-28T16:00:00Z',
      billing_mode: 'collect_now',
      payment_method_status: 'confirmed',
      billing_link_sent_at: null,
      payment_confirmed_at: '2026-02-28T16:20:00Z',
      status: 'confirmed',
    },
    campaign: {
      ...KFIRE_CAMPAIGNS[0],
      proposalId: 'prop-kfire-live',
      launched_at: '2026-03-01T09:00:00Z',
      proposalVersion: 1,
    },
  },
  'bp-kfire-001': {
    business: {
      id: 'biz-kfire',
      name: 'KFire Korean BBQ',
      address: '2528 N Milwaukee Ave, Chicago, IL 60647',
      category: 'Korean Restaurant',
      scope: 'single_location',
      rating: 4.6,
      reviews: 342,
      website_url: 'https://kfirebbq.com',
      createdAt: '2026-03-01T10:00:00Z',
    },
    businessProfile: {
      id: 'biz-kfire',
      place_id: 'mock-place-kfire-001',
      website_url: 'https://kfirebbq.com',
      name: 'KFire Korean BBQ',
      address: '2528 N Milwaukee Ave, Chicago, IL 60647',
      location_geo: null,
      category: 'Korean Restaurant',
      photos: [KFIRE_THUMBNAIL_URL],
      logo_url: null,
      reviews: 342,
      rating: 4.6,
      phone: '(773) 555-2528',
    },
    creatives: [],
    proposals: [],
    agreement: null,
    campaign: null,
  },
  'biz-hearth': {
    business: {
      id: 'biz-hearth',
      name: STUDIO_HEARTH_BUSINESS.name,
      address: STUDIO_HEARTH_BUSINESS.address,
      category: STUDIO_HEARTH_BUSINESS.category,
      scope: 'single_location',
      rating: STUDIO_HEARTH_BUSINESS.rating,
      reviews: STUDIO_HEARTH_BUSINESS.reviews_count,
      website_url: STUDIO_HEARTH_BUSINESS.website,
      createdAt: '2026-02-26T15:00:00Z',
    },
    businessProfile: {
      id: 'biz-hearth',
      place_id: STUDIO_HEARTH_BUSINESS.placeId,
      website_url: STUDIO_HEARTH_BUSINESS.website,
      name: STUDIO_HEARTH_BUSINESS.name,
      address: STUDIO_HEARTH_BUSINESS.address,
      location_geo: null,
      category: STUDIO_HEARTH_BUSINESS.category,
      photos: STUDIO_HEARTH_PHOTOS.map((photo) => photo.data_url),
      logo_url: STUDIO_HEARTH_BUSINESS.logo_data_url,
      reviews: STUDIO_HEARTH_BUSINESS.reviews_count,
      rating: STUDIO_HEARTH_BUSINESS.rating,
      phone: STUDIO_HEARTH_BUSINESS.phone,
    },
    creatives: [
      {
        id: 'cr-hearth-hero',
        businessId: 'biz-hearth',
        status: 'approved',
        objective: 'awareness',
        tone: 'elegant',
        scope: 'single_location',
        format: 'ctv_16_9',
        duration: 30,
        thumbnailUrl: STUDIO_HEARTH_PHOTOS[0]?.data_url || '/.mock/hearth_1.png',
        shareUrl: 'https://cornercast.tv/preview/cr-hearth-hero',
        assets: [
          {
            type: 'video',
            url: STUDIO_HEARTH_VIDEO_URL,
            thumbnail_url: STUDIO_HEARTH_PHOTOS[0]?.data_url || '/.mock/hearth_1.png',
            label: '30s CTV Spot',
          },
        ],
        proposalIds: ['prop-hearth-1', 'prop-hearth-2'],
        createdAt: '2026-02-27T16:30:00Z',
      },
      {
        id: 'cr-hearth-brunch',
        businessId: 'biz-hearth',
        status: 'ready',
        objective: 'seasonal',
        tone: 'friendly',
        scope: 'single_location',
        format: 'ctv_16_9',
        duration: 15,
        thumbnailUrl: STUDIO_HEARTH_PHOTOS[1]?.data_url || '/.mock/hearth_2.png',
        shareUrl: 'https://cornercast.tv/preview/cr-hearth-brunch',
        assets: [
          {
            type: 'video',
            url: '/.mock/7189ad10-f4d2-49a7-8629-452ab1fc9f2a.mp4',
            thumbnail_url: STUDIO_HEARTH_PHOTOS[1]?.data_url || '/.mock/hearth_2.png',
            label: '15s Brunch Spot',
          },
        ],
        proposalIds: ['prop-hearth-2'],
        createdAt: '2026-03-08T10:15:00Z',
      },
    ],
    proposals: MOCK_PROPOSALS['biz-hearth'],
    agreement: {
      id: 'agr-hearth-1',
      proposalId: 'prop-hearth-1',
      accepted_at: '2026-02-28T18:00:00Z',
      billing_mode: 'collect_now',
      payment_method_status: 'confirmed',
      billing_link_sent_at: null,
      payment_confirmed_at: '2026-02-28T18:15:00Z',
      status: 'confirmed',
    },
    campaign: {
      ...HEARTH_CAMPAIGNS[0],
      proposalId: 'prop-hearth-1',
    },
  },
};

const cloneMockSeed = (value) => JSON.parse(JSON.stringify(value));

export const getMockBusinessSeed = (businessId) => {
  const normalizedBusinessId = businessId === 'bp-kfire-001' ? 'biz-kfire' : businessId;
  const seed = MOCK_BUSINESS_SEEDS[normalizedBusinessId];
  return seed ? cloneMockSeed(seed) : null;
};

/** Mock industry benchmarks (for DealReport comparison) */
export const MOCK_BENCHMARKS = {
  cpm: 15.00,
  households_per_1k: 3200,
  impressions_per_1k: 9500,
};

// ── Phase 12: Deal scoring + intelligence helpers ──────────────────────────

/** Priority configuration for deal scoring */
export const PRIORITY_CONFIG = {
  high: { label: 'High priority', className: 'bg-coral/10 text-coral border-coral/20' },
  act_soon: { label: 'Act soon', className: 'bg-amber-100 text-amber-700 border-amber-200' },
  monitor: { label: 'Monitor', className: 'bg-blue-100 text-blue-700 border-blue-200' },
  low: { label: 'Low priority', className: 'bg-zinc-100 text-zinc-500 border-zinc-200' },
};

/**
 * Compute a composite deal priority score (0–100).
 *
 * Combines 4 signals:
 *   - Recency (0–25): hours since trigger → exponential decay
 *   - Engagement (0–25): workspace progression stage
 *   - Deal potential (0–25): rating × category value heuristic
 *   - Urgency (0–25): trigger type weight
 *
 * Returns { score, priorityLabel, priorityColor, priorityKey }
 */
export function computeDealScore(lead, businesses, creatives, proposals) {
  let recency = 0;
  let engagement = 0;
  let potential = 0;
  let urgency = 0;

  // 1. Recency (0–25): exponential decay from trigger date
  const hoursSince = (Date.now() - new Date(lead.triggerDate).getTime()) / (1000 * 60 * 60);
  if (hoursSince < 12) recency = 25;
  else if (hoursSince < 24) recency = 22;
  else if (hoursSince < 48) recency = 18;
  else if (hoursSince < 72) recency = 14;
  else if (hoursSince < 120) recency = 10;
  else if (hoursSince < 168) recency = 6;
  else recency = 2;

  // 2. Engagement (0–25): workspace progression
  const bizId = lead.businessProfileId;
  const bProposals = proposals?.[bizId] || [];
  const bCreatives = creatives?.[bizId] || [];

  if (bProposals.some((p) => p.status === 'accepted')) engagement = 25;
  else if (bProposals.some((p) => p.status === 'sent')) engagement = 20;
  else if (bProposals.some((p) => p.status === 'draft')) engagement = 15;
  else if (bCreatives.length > 0) engagement = 10;
  else if (businesses?.[bizId]) engagement = 5;
  else engagement = 0;

  // 3. Deal potential (0–25): rating × category value
  const rating = lead.businessRating || 3.5;
  const ratingScore = Math.min(15, Math.round((rating / 5) * 15));
  // High-value categories get bonus
  const cat = (lead.businessCategory || '').toLowerCase();
  let catBonus = 5; // default mid-tier
  if (/auto|car|dealer|financial|loan|insurance|medical|dental|legal|home service/i.test(cat)) catBonus = 10; // high ticket
  else if (/restaurant|food|bar|cafe|bakery|pizza|bbq|deli|florist|salon|fitness|gym/i.test(cat)) catBonus = 5; // high volume

  potential = Math.min(25, ratingScore + catBonus);

  // 4. Urgency (0–25): trigger type weight
  const trigger = (lead.triggerEvent || '').toLowerCase();
  if (/seasonal|push|promo/i.test(trigger)) urgency = 22;
  else if (/competitor.?drop/i.test(trigger)) urgency = 20;
  else if (/increased.?spend/i.test(trigger)) urgency = 18;
  else if (/first[- ]time/i.test(trigger)) urgency = 15;
  else if (/new radio|campaign/i.test(trigger)) urgency = 12;
  else urgency = 8;

  const score = recency + engagement + potential + urgency;

  let priorityKey = 'low';
  if (score >= 75) priorityKey = 'high';
  else if (score >= 50) priorityKey = 'act_soon';
  else if (score >= 25) priorityKey = 'monitor';

  return {
    score,
    priorityKey,
    priorityLabel: PRIORITY_CONFIG[priorityKey].label,
    priorityClassName: PRIORITY_CONFIG[priorityKey].className,
  };
}

/**
 * Determine the next-best-action for a lead based on workspace state.
 *
 * Returns { action, label, description, icon }
 */
export function getNextBestAction(lead, businesses, creatives, proposals) {
  const bizId = lead.businessProfileId;
  const biz = businesses?.[bizId];
  const bCreatives = creatives?.[bizId] || [];
  const bProposals = proposals?.[bizId] || [];
  const name = lead.businessName;

  // Campaign live
  if (bProposals.some((p) => p.status === 'accepted')) {
    return { action: 'monitor', label: 'Monitor campaign', description: `Campaign is live — check performance for ${name}` };
  }

  // Proposal sent — check follow-up timing
  const sentProposal = bProposals.find((p) => p.status === 'sent');
  if (sentProposal) {
    const daysSinceSent = Math.round((Date.now() - new Date(sentProposal.created_at).getTime()) / (1000 * 60 * 60 * 24));
    if (daysSinceSent > 3) {
      return { action: 'follow_up', label: `Follow up`, description: `It's been ${daysSinceSent} days — follow up with ${name}` };
    }
    return { action: 'wait', label: 'Awaiting response', description: `Proposal sent ${daysSinceSent} day${daysSinceSent !== 1 ? 's' : ''} ago — give it time` };
  }

  // Proposal drafted
  if (bProposals.some((p) => p.status === 'draft')) {
    return { action: 'send_proposal', label: 'Send proposal', description: `Your proposal is ready — send it to ${name}` };
  }

  // Creative exists, no proposal
  if (bCreatives.length > 0) {
    return { action: 'build_offer', label: 'Build offer', description: `Create a commercial offer for ${name}` };
  }

  // Business added, no creative
  if (biz) {
    return { action: 'generate_creative', label: 'Generate creative', description: `Build a sample ad to pitch ${name}` };
  }

  // No business yet
  return { action: 'add_business', label: 'Start deal', description: `Search and add ${name} to start the deal` };
}

/**
 * Compute stage breakdown from leads + workspace data.
 * Returns [{ stage, label, count, value }]
 */
export function computeStageBreakdown(leads, businesses, creatives, proposals) {
  const stages = [
    { stage: 'discovery', label: 'Discovery', count: 0, value: 0 },
    { stage: 'creative', label: 'Creative', count: 0, value: 0 },
    { stage: 'proposal', label: 'Proposal', count: 0, value: 0 },
    { stage: 'closed', label: 'Closed', count: 0, value: 0 },
  ];

  leads.forEach((lead) => {
    if (lead.status === 'closed') {
      if (lead.closedReason === 'won') {
        stages[3].count++;
        const valStr = (lead.dealValue || '$0').replace(/[^0-9]/g, '');
        stages[3].value += parseInt(valStr, 10) || 0;
      }
      return;
    }

    const bizId = lead.businessProfileId;
    const bProposals = proposals?.[bizId] || [];
    const bCreatives = creatives?.[bizId] || [];
    const biz = businesses?.[bizId];
    const valStr = estimateDealValue(lead).replace(/[^0-9]/g, '');
    const val = parseInt(valStr, 10) || 0;

    if (bProposals.some((p) => ['draft', 'sent', 'viewed', 'changes_requested'].includes(p.status))) {
      stages[2].count++;
      stages[2].value += val;
    } else if (bCreatives.length > 0 || biz) {
      stages[1].count++;
      stages[1].value += val;
    } else {
      stages[0].count++;
      stages[0].value += val;
    }
  });

  return stages;
}

/**
 * Compute days since last meaningful activity for a lead.
 * Returns { days, isStale }
 */
export function getStaleDays(lead, businesses, creatives, proposals) {
  const bizId = lead.businessProfileId;
  const biz = businesses?.[bizId];
  const bCreatives = creatives?.[bizId] || [];
  const bProposals = proposals?.[bizId] || [];

  // Find the most recent timestamp from all activity
  const dates = [lead.triggerDate, lead.createdAt];
  if (biz?.createdAt) dates.push(biz.createdAt);
  bCreatives.forEach((c) => { if (c.createdAt) dates.push(c.createdAt); });
  bProposals.forEach((p) => { if (p.created_at) dates.push(p.created_at); });

  const latest = dates.reduce((max, d) => {
    const t = new Date(d).getTime();
    return t > max ? t : max;
  }, 0);

  const days = Math.round((Date.now() - latest) / (1000 * 60 * 60 * 24));
  return { days, isStale: days > 7 };
}

/**
 * Get the current funnel stage index (0-3) for a lead.
 * Used for mini progress indicators.
 */
export function getFunnelStageIndex(lead, businesses, creatives, proposals) {
  if (lead.status === 'closed' && lead.closedReason === 'won') return 3;

  const bizId = lead.businessProfileId;
  const bProposals = proposals?.[bizId] || [];
  const bCreatives = creatives?.[bizId] || [];
  const biz = businesses?.[bizId];

  if (bProposals.length > 0) return 2;
  if (bCreatives.length > 0 || biz) return 1;
  return 0;
}

/** Mock campaign metrics */
export const MOCK_CAMPAIGN_METRICS = {
  status: 'live',
  spend: 1247,
  budget_cap: 2500,
  households_reached: 8420,
  impressions: 24650,
  cpm: 12.50,
  start_date: '2026-03-01',
  end_date: '2026-03-15',
  days_remaining: 9,
};
